(function () {
  // Ensure Luxon is loaded
  if (typeof luxon === "undefined") {
    console.error("Luxon library is not loaded. Please include it via CDN.");
    return;
  }

  const { DateTime } = luxon;

  // Configuration
  const NEXT_DAY_VARIANT_ID = 49479734067484;
  const NAMED_DAY_VARIANT_ID = 49479734100252;
  const SATURDAY_VARIANT_ID = 49503121047836;
  const DEBUG_DELIVERY_EXPIRED = false;

  // Force system to always use Europe/London time for all date calculations
  const TIME_ZONE = "Europe/London";

  // Get theme settings if available
  let MINIMUM_CUTOFF_DAYS = 1;
  let CUTOFF_HOUR = 11;
  let CUTOFF_MINUTE = 45;
  let BLOCKED_DATES = [];

  // Try to get settings from theme
  try {
    if (typeof theme !== "undefined" && theme.deliverySettings) {
      MINIMUM_CUTOFF_DAYS =
        parseInt(theme.deliverySettings.minimumCutoffDays) || 1;
      CUTOFF_HOUR = parseInt(theme.deliverySettings.cutoffHour) || 11;
      CUTOFF_MINUTE = parseInt(theme.deliverySettings.cutoffMinute) || 45;

      if (theme.deliverySettings.blockedDates) {
        // Parse blocked dates from DD/MM/YYYY format
        BLOCKED_DATES = theme.deliverySettings.blockedDates
          .split("\n")
          .map((date) => date.trim())
          .filter((date) => date.length > 0)
          .map((date) => {
            // Parse DD/MM/YYYY format
            const [day, month, year] = date
              .split("/")
              .map((num) => parseInt(num, 10));
            // Create ISO date string in YYYY-MM-DD format
            return `${year}-${month.toString().padStart(2, "0")}-${day
              .toString()
              .padStart(2, "0")}`;
          });
        log("Parsed blocked dates:", BLOCKED_DATES);
      }
    }
  } catch (error) {
    console.error("Error loading delivery settings from theme:", error);
  }

  // State
  let activeFlatpickrInstance = null;
  let deliveryExpiredHandled = false;
  let isRemovingDelivery = false;
  let isCartUpdateFromDelivery = false; // Add flag to track if update is from delivery removal

  // Utility functions
  function log(message, data) {
    console.log(`[cart-page] ${message}`, data);
  }

  function isBlockedDate(date) {
    // Check if date is in the blocked dates list from theme settings
    const dateISOString = date.toISODate();
    // Log the comparison for debugging
    log("Checking blocked date:", {
      date: dateISOString,
      blockedDates: BLOCKED_DATES,
      isBlocked: BLOCKED_DATES.includes(dateISOString),
    });
    return BLOCKED_DATES.includes(dateISOString);
  }

  function getNextWorkingDay(date) {
    let nextDay = date.plus({ days: 1 });

    // Keep adding days until we find a working day (not weekend or blocked date)
    while (
      nextDay.weekday === 6 ||
      nextDay.weekday === 7 ||
      isBlockedDate(nextDay)
    ) {
      nextDay = nextDay.plus({ days: 1 });
    }

    return nextDay;
  }

  // --- Debounce utility ---
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // --- Adjusted getMinimumDate ---
  function getMinimumDate() {
    const currentDate = DateTime.now().setZone(TIME_ZONE);
    log("Current date (local):", DateTime.local().toString());
    log("Current date (Europe/London):", currentDate.toString());
    log("Current time zone offset:", currentDate.offset / 60);
    const cutoffTime = DateTime.fromObject(
      {
        year: currentDate.year,
        month: currentDate.month,
        day: currentDate.day,
        hour: CUTOFF_HOUR,
        minute: CUTOFF_MINUTE,
        second: 0,
        millisecond: 0,
      },
      { zone: TIME_ZONE }
    );
    log("Cutoff time:", cutoffTime.toString());
    log("Is past cutoff:", currentDate > cutoffTime);
    const isPastCutoff = currentDate > cutoffTime;
    let minimumDate = currentDate;
    let workingDaysNeeded = Math.max(1, MINIMUM_CUTOFF_DAYS);
    if (isPastCutoff) {
      workingDaysNeeded = Math.max(2, MINIMUM_CUTOFF_DAYS);
    }
    let foundDays = 0;
    while (foundDays < workingDaysNeeded) {
      minimumDate = minimumDate.plus({ days: 1 });
      // Both candidate day and previous day must be working days (not Sunday, not blocked)
      if (
        minimumDate.weekday === 7 ||
        isBlockedDate(minimumDate) ||
        minimumDate.minus({ days: 1 }).weekday === 7 ||
        isBlockedDate(minimumDate.minus({ days: 1 }))
      ) {
        continue;
      }
      foundDays++;
    }
    minimumDate = minimumDate.startOf("day");
    log("Minimum date:", minimumDate.toISO());
    return minimumDate;
  }

  function isNextDay(selectedDate) {
    const currentDate = DateTime.now().setZone(TIME_ZONE).startOf("day");
    const nextDay = currentDate.plus({ days: 1 });
    const selectedDateTime = DateTime.fromJSDate(selectedDate)
      .setZone(TIME_ZONE)
      .startOf("day");

    // Add debug logs to troubleshoot next day detection
    log("Next day check:", {
      current: currentDate.toISO(),
      nextDay: nextDay.toISO(),
      selected: selectedDateTime.toISO(),
      isNextDay: nextDay.equals(selectedDateTime),
    });

    return nextDay.equals(selectedDateTime);
  }

  function isSaturday(selectedDate) {
    return DateTime.fromJSDate(selectedDate).setZone(TIME_ZONE).weekday === 6;
  }

  function isSampleLineItem(item) {
    if (typeof SampleOrder !== "undefined") {
      return SampleOrder.isSampleLineItem(item);
    }
    return item.variant_id === 49538592735516;
  }

  function isSampleOrder(cart) {
    const deliveryVariants = [
      NEXT_DAY_VARIANT_ID,
      NAMED_DAY_VARIANT_ID,
      SATURDAY_VARIANT_ID,
    ];
    return cart.items.every(
      (item) =>
        isSampleLineItem(item) ||
        deliveryVariants.includes(item.variant_id)
    );
  }

  function updateDeliveryMessage(cart) {
    const messageElement = document.getElementById("delivery-date-message");
    if (!messageElement) {
      console.error("Delivery date message element not found");
      return;
    }

    if (isSampleOrder(cart)) {
      messageElement.textContent =
        "Delivery date selection is not available for sample orders.";
      messageElement.style.display = "block";
    } else {
      messageElement.textContent = "";
      messageElement.style.display = "none";
    }
  }

  function hasOnlySamples(cart) {
    return cart.items.every((item) => isSampleLineItem(item));
  }

  function enableDatePicker() {
    const datePickerInput = document.getElementById("delivery-date");

    if (datePickerInput) {
      datePickerInput.disabled = false;
      datePickerInput.classList.remove("disabled");
    }
  }

  function disableDatePicker() {
    const datePickerInput = document.getElementById("delivery-date");

    if (datePickerInput) {
      datePickerInput.disabled = true;
      datePickerInput.value = "";
      datePickerInput.classList.add("disabled");
    }
  }

  function removeSampleOrderDelivery(cart) {
    // If cart has only samples, or if all items are either samples or delivery add-ons
    if (
      hasOnlySamples(cart) ||
      (cart.items.length > 0 &&
        cart.items.every(
          (item) =>
            isSampleLineItem(item) ||
            [
              NEXT_DAY_VARIANT_ID,
              NAMED_DAY_VARIANT_ID,
              SATURDAY_VARIANT_ID,
            ].includes(item.variant_id)
        )) ||
      // Add check for when cart only has samples and delivery
      (cart.items.length === 2 &&
        cart.items.some((item) => isSampleLineItem(item)) &&
        cart.items.some((item) =>
          [
            NEXT_DAY_VARIANT_ID,
            NAMED_DAY_VARIANT_ID,
            SATURDAY_VARIANT_ID,
          ].includes(item.variant_id)
        ))
    ) {
      log("Removing delivery from sample order cart:", cart);

      // Check if there's actually a delivery item to remove
      const hasDeliveryItem = cart.items.some(
        (item) =>
          item.variant_id === NEXT_DAY_VARIANT_ID ||
          item.variant_id === NAMED_DAY_VARIANT_ID ||
          item.variant_id === SATURDAY_VARIANT_ID
      );

      if (hasDeliveryItem) {
        return removeDeliveryFromCart().then(() => {
          // Ensure the UI is updated
          log("Delivery item removed from sample cart, triggering update");
          location.reload();
          return cart;
        });
      } else {
        return Promise.resolve(cart);
      }
    }
    return Promise.resolve(cart);
  }

  function checkDeliveryDateExpiration(cart) {
    // Only for debugging
    if (DEBUG_DELIVERY_EXPIRED) {
      return removeDeliveryFromCart().then(() => {
        location.reload();
        return true;
      });
    }

    const hasDeliveryItem = cart.items.some(
      (item) =>
        item.variant_id === NEXT_DAY_VARIANT_ID ||
        item.variant_id === NAMED_DAY_VARIANT_ID ||
        item.variant_id === SATURDAY_VARIANT_ID
    );

    if (!hasDeliveryItem) return Promise.resolve(false);

    const deliveryDateStr = cart.attributes && cart.attributes["Delivery Date"];
    if (!deliveryDateStr) return Promise.resolve(false);

    const deliveryDate = DateTime.fromISO(deliveryDateStr).setZone(TIME_ZONE);
    const currentDate = DateTime.now().setZone(TIME_ZONE);
    const minimumDate = getMinimumDate();

    log("Checking delivery expiration:", {
      deliveryDate: deliveryDate.toISO(),
      currentDate: currentDate.toISO(),
      minimumDate: minimumDate.toISO(),
    });

    if (deliveryDate < minimumDate) {
      log("Delivery date expired:", {
        deliveryDate: deliveryDate.toISO(),
        minimumDate: minimumDate.toISO(),
      });

      if (!deliveryExpiredHandled) {
        deliveryExpiredHandled = true;
        return removeDeliveryFromCart().then(() => {
          // Make sure the UI updates after removing delivery
          location.reload();
          setTimeout(() => {
            alert(
              "Your selected delivery date is no longer available. Please select a new delivery date."
            );
          }, 100);
          return true;
        });
      }

      return Promise.resolve(true);
    }

    return Promise.resolve(false);
  }

  function updateDeliveryInCart(selectedDate) {
    const nextDay = isNextDay(selectedDate);
    const saturday = isSaturday(selectedDate);
    let variantId;
    if (saturday) {
      variantId = SATURDAY_VARIANT_ID;
    } else if (nextDay) {
      variantId = NEXT_DAY_VARIANT_ID;
    } else {
      variantId = NAMED_DAY_VARIANT_ID;
    }

    const formattedDateISO = DateTime.fromJSDate(selectedDate)
      .setZone(TIME_ZONE)
      .toISODate();

    const formattedDateDisplay = DateTime.fromJSDate(selectedDate)
      .setZone(TIME_ZONE)
      .toFormat("dd/MM/yyyy");

    log("Updating cart with delivery:", {
      dateISO: formattedDateISO,
      dateDisplay: formattedDateDisplay,
      nextDay,
      saturday,
      variantId,
    });

    return fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        const updates = {};
        let deliveryInCart = false;
        let deliveryLineItemKey = null;

        cart.items.forEach((item) => {
          if (
            item.variant_id === NEXT_DAY_VARIANT_ID ||
            item.variant_id === NAMED_DAY_VARIANT_ID ||
            item.variant_id === SATURDAY_VARIANT_ID
          ) {
            if (item.variant_id === variantId) {
              updates[item.key] = 1;
              deliveryInCart = true;
              deliveryLineItemKey = item.key;
            } else {
              updates[item.key] = 0;
            }
          } else {
            updates[item.key] = item.quantity;
          }
        });

        const cartUpdatePayload = {
          updates: updates,
          attributes: {
            "Delivery Date": formattedDateISO,
            date: "", // Clear the old date attribute
          },
        };

        if (!deliveryInCart) {
          // First add the new delivery item
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: { "Delivery Date": formattedDateDisplay },
                },
              ],
            }),
          })
            .then(() => fetch("/cart.js")) // Get updated cart
            .then((response) => response.json())
            .then((updatedCart) => {
              // Then update quantities and attributes
              return fetch("/cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(cartUpdatePayload),
              }).then(() => updatedCart);
            });
        } else {
          // Update existing delivery item
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(cartUpdatePayload),
          })
            .then(() => {
              if (deliveryLineItemKey) {
                return fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    id: deliveryLineItemKey,
                    quantity: 1,
                    properties: { "Delivery Date": formattedDateDisplay },
                  }),
                });
              }
            })
            .then(() => fetch("/cart.js"))
            .then((response) => response.json());
        }
      })
      .then((updatedCart) => {
        log("Cart updated with new delivery:", updatedCart);
        // Trigger cart update to refresh the cart display
        location.reload();
      })
      .catch((error) =>
        console.error("Error updating delivery in cart:", error)
      );
  }

  function removeDeliveryFromCart() {
    // Prevent recursive calls
    if (isRemovingDelivery) return Promise.resolve();
    isRemovingDelivery = true;

    return fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        // If cart is empty or has no delivery items, don't proceed
        if (!cart.items || cart.items.length === 0) {
          isRemovingDelivery = false;
          disableDatePicker();
          return cart;
        }

        const hasDeliveryItem = cart.items.some(
          (item) =>
            item.variant_id === NEXT_DAY_VARIANT_ID ||
            item.variant_id === NAMED_DAY_VARIANT_ID ||
            item.variant_id === SATURDAY_VARIANT_ID
        );

        if (!hasDeliveryItem) {
          isRemovingDelivery = false;
          disableDatePicker();
          return cart;
        }

        const updates = {};
        cart.items.forEach((item) => {
          if (
            item.variant_id !== NEXT_DAY_VARIANT_ID &&
            item.variant_id !== NAMED_DAY_VARIANT_ID &&
            item.variant_id !== SATURDAY_VARIANT_ID
          ) {
            updates[item.key] = item.quantity;
          } else {
            updates[item.key] = 0;
          }
        });

        // First update quantities to remove delivery items
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updates: updates,
          }),
        })
          .then(() => {
            // Then clear the date metafields
            return fetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                attributes: {
                  "Delivery Date": "",
                  date: "",
                },
              }),
            });
          })
          .then((response) => response.json());
      })
      .then((updatedCart) => {
        log("Delivery removed from cart:", updatedCart);
        isRemovingDelivery = false;
        disableDatePicker();
        // Trigger cart update to refresh the cart display
        location.reload();
      })
      .catch((error) => {
        console.error("Error removing delivery from cart:", error);
        isRemovingDelivery = false;
        disableDatePicker();
      });
  }

  function hasDeliveryProduct(cart) {
    return cart.items.some(
      (item) =>
        item.variant_id === NEXT_DAY_VARIANT_ID ||
        item.variant_id === NAMED_DAY_VARIANT_ID ||
        item.variant_id === SATURDAY_VARIANT_ID
    );
  }

  function removeDeliveryDateMetafield() {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          "Delivery Date": "",
          date: "", // Also remove the old date attribute
        },
      }),
    });
  }

  function initializeDatePicker(cart) {
    const datePickerInput = document.getElementById("delivery-date");
    if (!datePickerInput) {
      console.error("Required elements for date picker not found");
      return Promise.resolve(null);
    }

    updateDeliveryMessage(cart);

    if (isSampleOrder(cart)) {
      disableDatePicker();
      return Promise.resolve(null);
    }

    const hasDeliveryItem = cart.items.some(
      (item) =>
        item.variant_id === NEXT_DAY_VARIANT_ID ||
        item.variant_id === NAMED_DAY_VARIANT_ID ||
        item.variant_id === SATURDAY_VARIANT_ID
    );

    let savedDateISO =
      cart.attributes && hasDeliveryItem
        ? cart.attributes["Delivery Date"]
        : null;
    let parsedSavedDate = null;

    if (savedDateISO) {
      parsedSavedDate = DateTime.fromISO(savedDateISO)
        .setZone(TIME_ZONE)
        .toJSDate(); // Parse ISO string back to JS Date object
      if (!isNaN(parsedSavedDate)) {
        datePickerInput.value =
          DateTime.fromISO(savedDateISO).toFormat("dd/MM/yyyy"); // Display in d/m/y format
      }
    }

    const minimumDate = getMinimumDate().toJSDate();
    log("Minimum date passed to flatpickr:", minimumDate);

    const flatpickrInstance = flatpickr(datePickerInput, {
      enableTime: false,
      dateFormat: "d/m/Y", // For display only
      minDate: minimumDate,
      defaultDate: parsedSavedDate,
      locale: {
        firstDayOfWeek: 1,
      },
      disable: [
        function (date) {
          // Convert JS Date to Luxon DateTime for consistent handling
          const luxonDate = DateTime.fromJSDate(date).setZone(TIME_ZONE);

          // Disable Sundays (weekday 7 in Luxon)
          if (luxonDate.weekday === 7) return true;

          // Check if date is a blocked date from theme settings
          if (isBlockedDate(luxonDate)) {
            log("Date is blocked:", luxonDate.toISODate());
            return true;
          }

          // Check if date is before the minimum allowed date
          const minDate = getMinimumDate();
          if (luxonDate < minDate) return true;

          return false;
        },
      ],
      onChange: function (selectedDates, dateString, instance) {
        if (selectedDates.length > 0) {
          const selectedDate = selectedDates[0];
          datePickerInput.value = instance.formatDate(selectedDate, "d/m/Y");
          updateDeliveryInCart(selectedDate).catch(console.error); // Store as ISO
        } else {
          datePickerInput.value = "";
          removeDeliveryFromCart()
            .then(() => {
              // Ensure UI updates when delivery is removed
              location.reload();
            })
            .catch(console.error);
        }
      },
    });

    return Promise.resolve(flatpickrInstance);
  }

  function initialize() {
    if (deliveryExpiredHandled) {
      log("Delivery already expired, skipping initialization");
      return;
    }

    log("Initializing date picker");

    if (activeFlatpickrInstance) {
      log("Destroying existing flatpickr instance");
      activeFlatpickrInstance.destroy();
      activeFlatpickrInstance = null;
    }

    // Log the initialization time to help with debugging
    log("Initializing at:", {
      local: DateTime.local().toString(),
      now: DateTime.now().toString(),
      londonTime: DateTime.now().setZone(TIME_ZONE).toString(),
      offset: DateTime.now().setZone(TIME_ZONE).offset / 60,
    });

    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        log("Fetched cart for initialization:", cart);

        // Check if there's a Delivery Date metafield but no delivery product
        const hasDeliveryItem = cart.items.some(
          (item) =>
            item.variant_id === NEXT_DAY_VARIANT_ID ||
            item.variant_id === NAMED_DAY_VARIANT_ID ||
            item.variant_id === SATURDAY_VARIANT_ID
        );

        if (
          cart.attributes &&
          cart.attributes["Delivery Date"] &&
          !hasDeliveryItem
        ) {
          log(
            "Found Delivery Date metafield without delivery product, removing it"
          );
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              attributes: {
                "Delivery Date": "",
                date: "",
              },
            }),
          }).then(() => {
            // After removing the metafield, reload the page to ensure clean state
            location.reload();
            return cart;
          });
        }

        return cart;
      })
      .then((cart) => {
        updateDeliveryMessage(cart);
        if (!cart.items || cart.items.length === 0) {
          log("Cart is empty, disabling date picker");
          disableDatePicker();
          return cart;
        }
        return removeSampleOrderDelivery(cart);
      })
      .then((updatedCart) => {
        if (!updatedCart.items || updatedCart.items.length === 0) {
          log(
            "Cart is empty after sample order delivery check, disabling date picker"
          );
          disableDatePicker();
          return;
        }

        if (!isSampleOrder(updatedCart)) {
          log("Initializing date picker for non-sample order");
          return checkDeliveryDateExpiration(updatedCart).then((isExpired) => {
            if (!isExpired) {
              enableDatePicker();
              return initializeDatePicker(updatedCart).then((newInstance) => {
                activeFlatpickrInstance = newInstance;
                log(
                  "Date picker initialized with new instance",
                  newInstance ? "success" : "failed"
                );
              });
            } else {
              log("Delivery date expired, skipping date picker initialization");
              disableDatePicker();
            }
          });
        } else {
          log("Sample order detected, disabling date picker");
          disableDatePicker();
        }
      })
      .catch((error) => {
        console.error("Error fetching or updating cart:", error);
        disableDatePicker();
      });
  }

  // --- Debounced versions of initialize and cutOffTimeCheck ---
  const debouncedInitialize = debounce(initialize, 300);
  const debouncedCutOffTimeCheck = debounce(cutOffTimeCheck, 300);

  // --- Use debounced functions in event listeners and timer ---
  document.addEventListener("DOMContentLoaded", debouncedInitialize);
  document.addEventListener("cart:rerendered", () => {
    if (isRemovingDelivery) return;
    log(
      "Cart rerendered event triggered, checking if datepicker needs reinitializing"
    );
    if (activeFlatpickrInstance) {
      log("Destroying existing flatpickr instance");
      activeFlatpickrInstance.destroy();
      activeFlatpickrInstance = null;
    }
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        log("Fetched cart for reinitializing:", cart);
        updateDeliveryMessage(cart);
        if (cart.items && cart.items.length > 0) {
          return removeSampleOrderDelivery(cart);
        } else {
          disableDatePicker();
          return cart;
        }
      })
      .then((updatedCart) => {
        if (!updatedCart.items || updatedCart.items.length === 0) {
          log("Cart is empty, disabling date picker");
          disableDatePicker();
          return;
        }
        if (!isSampleOrder(updatedCart)) {
          log("Reinitializing date picker for non-sample order");
          return checkDeliveryDateExpiration(updatedCart).then((isExpired) => {
            if (!isExpired) {
              enableDatePicker();
              return initializeDatePicker(updatedCart).then((newInstance) => {
                activeFlatpickrInstance = newInstance;
                log(
                  "Date picker reinitialized with new instance",
                  newInstance ? "success" : "failed"
                );
              });
            } else {
              log(
                "Delivery date expired, skipping date picker reinitialization"
              );
              disableDatePicker();
            }
          });
        } else {
          log("Sample order detected, disabling date picker");
          disableDatePicker();
        }
      })
      .catch((error) => {
        console.error("Error checking cart state:", error);
        disableDatePicker();
      });
  });
  setInterval(() => {
    log("Periodic check: running cutOffTimeCheck()");
    debouncedCutOffTimeCheck();
  }, 60 * 1000); // every 60 seconds

  // --- New function for periodic cut-off time check ---
  function cutOffTimeCheck() {
    log("Running cutOffTimeCheck: checking cut-off and delivery date validity");
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        // Update minDate on the date picker if it exists
        if (activeFlatpickrInstance) {
          const newMinDate = getMinimumDate().toJSDate();
          activeFlatpickrInstance.set("minDate", newMinDate);

          // If the selected date is now invalid, clear it and remove delivery from cart
          const selectedDate = activeFlatpickrInstance.selectedDates[0];
          if (selectedDate && selectedDate < newMinDate) {
            activeFlatpickrInstance.clear();
            // Remove delivery product if present
            const hasDeliveryItem = cart.items.some(
              (item) =>
                item.variant_id === NEXT_DAY_VARIANT_ID ||
                item.variant_id === NAMED_DAY_VARIANT_ID ||
                item.variant_id === SATURDAY_VARIANT_ID
            );
            if (hasDeliveryItem) {
              removeDeliveryFromCart().then(() => {
                alert(
                  "Your selected delivery date is no longer available. Please select a new delivery date."
                );
              });
            }
          }
        } else {
          // If no date picker, still check for expired delivery date in cart
          checkDeliveryDateExpiration(cart);
        }
      })
      .catch((error) => {
        console.error("Error in cutOffTimeCheck:", error);
      });
  }
})();
